import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addReview = createAsyncThunk(
    'review/add',
    async ({ product_id, rating, comment }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('product_id', product_id);
            formData.append('rating', rating);
            formData.append('comment', comment);

            const response = await callAPI(
                apiUrls.reviewAdd,
                {},
                "POST",
                formData
            );

            if (response?.data?.status || response?.data?.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to add review');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchProductReviews = createAsyncThunk(
    'review/fetchByProduct',
    async (product_id, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.review.getByProduct}/${product_id}`,
                {},
                "GET"
            );

            if (response?.data?.status || response?.data?.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch reviews');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

export const fetchUserReviews = createAsyncThunk(
    'review/fetchUserReviews',
    async (_, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                apiUrls.review.getUserReviews,
                {},
                "GET"
            );

            if (response?.data?.status || response?.data?.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch user reviews');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

const reviewSlice = createSlice({
    name: 'review',
    initialState: {
        productReviews: {},
        userReviews: [],
        loading: false,
        submitting: false,
        error: null,
        success: false,
        totalReviews: 0,
        averageRating: 0
    },
    reducers: {
        clearReviewError: (state) => {
            state.error = null;
        },
        clearReviewSuccess: (state) => {
            state.success = false;
        },
        resetReviewState: (state) => {
            state.submitting = false;
            state.error = null;
            state.success = false;
        },
        setReviews: (state, action) => {
            state.productReviews = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(addReview.pending, (state) => {
            state.submitting = true;
            state.error = null;
            state.success = false;
        });
        builder.addCase(addReview.fulfilled, (state, action) => {
            state.submitting = false;
            state.success = true;

            const { product_id, review } = action.payload.data || {};
            if (product_id && review && state.productReviews[product_id]) {
                state.productReviews[product_id].reviews.unshift(review);
                state.productReviews[product_id].totalReviews += 1;
                const reviews = state.productReviews[product_id].reviews;
                const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
                state.productReviews[product_id].averageRating = totalRating / reviews.length;
            }
        });
        builder.addCase(addReview.rejected, (state, action) => {
            state.submitting = false;
            state.error = action.payload;
            state.success = false;
        });

        builder.addCase(fetchProductReviews.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchProductReviews.fulfilled, (state, action) => {
            state.loading = false;
            const { product_id, reviews, totalReviews, averageRating } = action.payload.data || {};

            if (product_id) {
                state.productReviews[product_id] = {
                    reviews: reviews || [],
                    totalReviews: totalReviews || 0,
                    averageRating: averageRating || 0,
                    lastUpdated: new Date().toISOString()
                };
            }
        });
        builder.addCase(fetchProductReviews.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(fetchUserReviews.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchUserReviews.fulfilled, (state, action) => {
            state.loading = false;
            state.userReviews = action.payload.data?.reviews || [];
            state.totalReviews = action.payload.data?.total || 0;
            state.averageRating = action.payload.data?.averageRating || 0;
        });
        builder.addCase(fetchUserReviews.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    }
});

export const {
    clearReviewError,
    clearReviewSuccess,
    resetReviewState,
    setReviews
} = reviewSlice.actions;

export const selectReviewLoading = (state) => state.review.loading;
export const selectReviewSubmitting = (state) => state.review.submitting;
export const selectReviewError = (state) => state.review.error;
export const selectReviewSuccess = (state) => state.review.success;
export const selectProductReviews = (productId) => (state) =>
    state.review.productReviews[productId] || { reviews: [], totalReviews: 0, averageRating: 0 };
export const selectUserReviews = (state) => state.review.userReviews;
export const selectTotalReviews = (state) => state.review.totalReviews;
export const selectAverageRating = (state) => state.review.averageRating;

export default reviewSlice.reducer;